import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {withRouter} from 'react-router';
import {connect} from 'react-redux';
import Helmet from 'react-helmet';
import config from 'config';
import {bindToSSO, updateLoginState} from 'modules/Common/reducers/skysso';
import LoginLightboxContainer from 'modules/Common/containers/Lightboxes/LoginLightboxContainer';
import MyStableLightboxContainer from 'modules/Common/containers/Lightboxes/MyStableLightboxContainer';
import JsonLD from 'modules/Common/components/JsonLD';
import withAnalytics from 'utils/withAnalytics';
import {watchMediaQuery} from 'utils/mediaQueryHelpers';
import {setBusy} from 'modules/Common/reducers/pageTranstition';
import {getEnabledFeatures, has} from 'utils/features';

import {canonicalLink, titleTemplate} from 'utils/seoUtils';
import InsiderNotification from 'modules/Insider/containers/InsiderNotification';
import CookieNotice from 'modules/Common/containers/CookieNotice';
import Footer from 'modules/Common/components/Footer';
import SplashContainer from 'modules/Common/components/SplashContainer';
import StickyMenu from 'modules/Common/components/StickyMenu';
import {Lightbox} from 'modules/Common/components/Lightbox';
import FeedbackForm from 'modules/Common/containers/FeedbackForm';
import {showFeedbackForm} from 'modules/Common/actions/feedbackForm';
import {destructBetslip} from 'modules/Common/reducers/betslip';
import {getStickyMenu} from 'utils/menuUtils';
import smoothscroll from 'smoothscroll-polyfill';
import PopOutPlayer from "../../Ooyala/component/PopOutPlayer";

const MENU_OPEN_CLASS = " main-nav-open";

function getClosestRouteProperty(props, propertyName, defaultValue) {
  return props.routes.reduce(
    (prev, curr) => (curr.hasOwnProperty(propertyName) ? curr[propertyName] : prev),
    defaultValue
  );
}

@connect(state => ({
  queuedBetLinks: state.common.betlink.queue,
  pageTranstition: state.common.pageTranstition,
  loggedIn: state.common.auth.loggedIn,
  showFeedbackForm: state.common.display.feedbackForm.show
}))
@withAnalytics
@withRouter
export default class App extends Component {

  static propTypes = {
    children: PropTypes.node.isRequired,
    routes: PropTypes.array.isRequired,
    router: PropTypes.object.isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired
    }).isRequired,
    dispatch: PropTypes.func.isRequired,
    queuedBetLinks: PropTypes.array,
    pageTranstition: PropTypes.shape({
      busy: PropTypes.bool.isRequired
    }).isRequired,
    loggedIn: PropTypes.bool,
    showFeedbackForm: PropTypes.bool.isRequired
  };

  constructor (...args) {
    super(...args);

    this.state = {
      featureClasses: [],
      betCount: 0, // only relevant for feature "integrated-betslip", leaving it zero for feature off ensures it's never displayed
      mainMenuOpen: false,
      subNavOpen: false,
      allSportsOpen: false,
      subNavMoreOpen: false,
      suppressStickyMenu: true,
      JsonLD: {
        "@type": "Organization",
        "@id":"https://www.sportinglife.com",
        name: "Sporting Life",
        legalName : config.structuredData.legalName,
        url: "https://www.sportinglife.com",
        logo: "https://www.sportinglife.com/logo.jpg",
        foundingDate: config.structuredData.founded,
        address: {
          "@type": "PostalAddress",
          streetAddress: config.structuredData.streetAddress,
          addressLocality: config.structuredData.city,
          addressRegion: config.structuredData.region,
          postalCode: config.structuredData.postalCode,
          addressCountry: "UK"
        },
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "customer support",
          email: config.structuredData.email,
          url: "https://www.sportinglife.com"
        },
        "sameAs": [
          "https://www.facebook.com/SportingLifeOfficial",
          "https://twitter.com/SportingLife",
          "https://soundcloud.com/sportinglife",
          "https://en.wikipedia.org/wiki/Sporting_Life_(British_newspaper)",
          "https://www.wikidata.org/wiki/Q7579580"
        ]
      }
    };

    this.removeDocumentClickToCloseAll = () => {}; // initial noop, gets reassigned by addDocumentClickToCloseAll
    // initial noops that get reassigned in componentDidMount
    this.unwatchTablet = () => {};
  }

  componentDidMount() {
    this.props.dispatch(bindToSSO());
    this.props.dispatch(setBusy(false));
    this.unwatchTablet = watchMediaQuery("tablet", () => {
      this.toggleSubNav(null, false); // Always close when the breakpoint changes, closing the subnav closes all menus
    });

    if ('serviceWorker' in navigator ) {
      if (navigator.serviceWorker) {
        navigator.serviceWorker.register('/service-worker.js', {scope: '/'}).then(function(registration) {
          if(!config.serviceWorkerEnabled) {
            registration.unregister();
          }
        }).catch(()=>{})
      }
    }

    smoothscroll.polyfill();

    this.setState({...this.state, featureClasses: getEnabledFeatures().map(f => `${f}-feature`)})

    // If the app lives for 24 hours, reload it
    window.setTimeout(() => {
      location.reload();
    }, 86400000);
  }

  componentWillUnmount() {
    this.unwatchTablet();

    // only relevant for feature "integrated-betslip"
    this.props.dispatch(destructBetslip());
  }

  componentWillReceiveProps(nextProps) {
    if ((this.props.pageTranstition.busy === true) && (nextProps.pageTranstition.busy === true)) {
      // Only set busy to false if it's currently true, and this update doesn't change it
      // (setting busy would also call componentWillReceiveProps, so checking for a change makes sure this update was caused by a different prop)
      // This way the dispatch wont be duplicated
      this.props.dispatch(setBusy(false));
    }

    this.props.dispatch(updateLoginState());

    this.toggleMainMenu(null, false);

    const routePreventScroll = getClosestRouteProperty(nextProps, "preventScroll", false);

    // location state might be null, so easiest is to split the destructuring in two
    const {
      location: {
        state: locationState
      } = {}
    } = nextProps;

    const {
      preventScroll: linkPreventedScroll = false
    } = locationState || {};

    if (!(routePreventScroll || linkPreventedScroll) && !nextProps.pageTranstition.busy) {
      this.toDefaultLocation();
    }
  }

  hideMenu() {
    const stickyMenu = getStickyMenu();
    if (stickyMenu) {
      stickyMenu.style.top = `${-stickyMenu.clientHeight}px`;
    }
  }

  addDocumentClickToCloseAll() {
    this.removeDocumentClickToCloseAll(); // remove any existing - there can be only one

    const closeAll = e => this.toggleMainMenu(e, false); // unique new function for this time

    document.addEventListener("click", closeAll, false); // add it

    this.removeDocumentClickToCloseAll = () => document.removeEventListener("click", closeAll); // store a function to remove it
  }

  html() {
    // memoise the value
    this.htmlElement = this.htmlElement || document.getElementsByTagName("html")[0];
    return this.htmlElement;
  }

  addHTMLMenuOpenClass() {
    this.html().className += MENU_OPEN_CLASS;
  }

  removeHTMLMenuOpenClass() {
    this.html().className = this.html().className.replace(MENU_OPEN_CLASS, "");
  }

  subnav = () => {
    // find the subnav component from the last route to have one defined, if any
    // ie find the subnav that's closest to the route in play
    return getClosestRouteProperty(this.props, "subnav", undefined);
  }

  toTop(e) {
    e && e.preventDefault();
    window.scrollTo(0, 0);
  }

  toTopSmooth(e) {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });


    e.preventDefault();
  }

  toDefaultLocation() {
    const { hash } = window.location;
    if (hash !== '') {
      const id = hash.replace('#', '');
      this.setState({suppressStickyMenu: true});
      this.hideMenu();
      const element = id != null? document.getElementById(id) : null;
      if (element) {
        element.scrollIntoView();
      } else {
        this.toTop();
      }
    } else {
      this.toTop();
    }
  }

  clickToCloseAll(state) {
    if (state) {
      this.addDocumentClickToCloseAll();
    } else {
      this.removeDocumentClickToCloseAll();
    }

    return state;
  }

  toggleMainMenu = (e, state = !this.state.mainMenuOpen) => {
    let allSportClicked = false;

    if (e) {
      if (e.target.getAttribute("target") !== "_blank") {
        e.preventDefault();
      }

      allSportClicked = e.target.hasAttribute("data-keep-menu-open");
    }

    const newState = {
      mainMenuOpen: state || allSportClicked,
      subNavOpen: false,
      subNavMoreOpen: false
    };

    if (!allSportClicked) {
      newState.allSportsOpen = false
    }

    this.setState(newState);

    this.removeHTMLMenuOpenClass();

    if (this.clickToCloseAll(newState.mainMenuOpen)) {
      this.addHTMLMenuOpenClass();
    }
  }

  toggleSubNav = (e, state = !this.state.subNavOpen) => {
    this.setState({
      mainMenuOpen: false,
      subNavOpen: state,
      allSportsOpen: false,
      subNavMoreOpen: false
    });

    this.removeHTMLMenuOpenClass();
    this.clickToCloseAll(state);
  }

  toggleAllSports = (e, state = !this.state.allSportsOpen) => {
    e && e.preventDefault();
    this.setState({
      allSportsOpen: state,
      subNavMoreOpen: false
    });
  }

  toggleSubNavMore = (e, state = !this.state.subNavMoreOpen) => {
    e.preventDefault();
    e.stopPropagation();

    this.setState({
      subNavMoreOpen: state,
      allSportsOpen: false
    });

    this.clickToCloseAll(state);
  }

  handleOpenFeedbackForm = event => {
    //stop a tag event from adding href to location etc..
    event.preventDefault()
    this.props.dispatch(showFeedbackForm(true))
  }

  render() {
    require('./App.scss');

    var LocalSubNav = this.subnav();

    // Doing props this way is only relevant for feature integrated-betslip
    // so that the class can be dynamically added
    // The class will be irrelevant once the feature is stripped, either way, so you can remove it

    const contentProps = {
      id: "content"
    }

    if (LocalSubNav) {
      contentProps.className = "withSubnav";
    }
    else {
      contentProps.className = "noSubnav";
    }

    if (has("integrated-betslip")) {
      contentProps.className += " integrated-betslip";
    }

    // end of integrated-betslip block
    return (
      <div id="container" className={this.state.featureClasses.join(' ')}>
          <Helmet
            titleTemplate={titleTemplate.plain}
            defaultTitle={`${config.pageTitle.default} - ${config.pageTitle.mainSuffix}`}
            {...canonicalLink(this.props.routes)}
          />
        <JsonLD data={this.state.JsonLD}/>
        {this.props.pageTranstition.busy && <div className="pageTranstition"/>}

        <div id="mobile-bet-basket"/>

        <StickyMenu
          subnav={LocalSubNav}
          location={this.props.location}
          loggedIn={this.props.loggedIn}
          router={this.props.router}
          featureClasses={this.state.featureClasses}
          toggleMainMenu={this.toggleMainMenu}
          mainMenuOpen={this.state.mainMenuOpen}
          allSportsOpen={this.state.allSportsOpen}
          subNavOpen={this.state.subNavOpen}
          subNavMoreOpen={this.state.subNavMoreOpen}
          toggleSubNav={this.toggleSubNav}
          toggleSubNavMore={this.toggleSubNavMore}
          toggleAllSports={this.toggleAllSports}
          navConfig={config.nav}
        />


        <LoginLightboxContainer />
        <MyStableLightboxContainer />

        <section {...contentProps}>
          <InsiderNotification />
          <CookieNotice policyLink={config.cookiePolicy.policyLink} />
          <SplashContainer />
          {this.props.children}
        </section>
        { this.props.showFeedbackForm &&
          <section className="feedback-lightbox">
            <Lightbox title="Sporting Life Feedback" close={() => this.props.dispatch(showFeedbackForm(false))} >
                <FeedbackForm />
            </Lightbox>
          </section>
        }
        <div id="footer">
          <Footer handleOpenFeedbackForm={this.handleOpenFeedbackForm} handleScrollTop={this.toTopSmooth} />
        </div>
        <PopOutPlayer />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./modules/App/containers/App.js